/**
 * Error-reporting configuration — the Sentry Loader Script's env inputs.
 *
 * Mirrors `lib/analytics/config.ts`: every value is `NEXT_PUBLIC_*` (shipped to
 * the browser by design) and gated on presence + format, so an unset or
 * malformed variable makes the whole feature a no-op instead of loading the
 * wrong thing. Read via functions (not module constants) so tests can set
 * `process.env.*` before calling; Next still inlines the `process.env.*`
 * literals at build time inside these functions.
 *
 * The format checks are load-bearing, not belt-and-braces: the URL is
 * interpolated into a `<script src>` and the environment name into an inline
 * `<script>` body (see `sentry-loader-script.tsx`), so a bad value would be a
 * JS/script injection rather than a misconfiguration.
 *
 * The enable rule copies the desktop app's `resolveSentryConfig`: outside
 * production nothing is reported unless someone opts in explicitly, so dev
 * servers, previews and the test suite never send anything to sentry.io.
 */
import { WEB_CLIENT_VERSION } from '../web-client-info'

/**
 * The Sentry Loader Script URL: `https://js.sentry-cdn.com/<public key>.min.js`,
 * or the German regional host `js-de.`. The public key is a 32-char hex DSN key.
 * Nothing else is accepted — this string becomes a script `src`.
 */
const SENTRY_LOADER_URL_PATTERN = /^https:\/\/js(-de)?\.sentry-cdn\.com\/[0-9a-f]{32}\.min\.js$/

/** Environment names are slugs. Anything else cannot reach the inline script. */
const SENTRY_ENVIRONMENT_PATTERN = /^[a-zA-Z0-9_-]{1,32}$/

/**
 * The Sentry release this build reports as. Derived from the one version string
 * apps/web already ships (`Client-Info`), so PR-3's source-map upload can name
 * the same release without a second source of truth.
 */
export const WEB_SENTRY_RELEASE = `kiao-voice-web@${WEB_CLIENT_VERSION}`

export interface ErrorReportingConfig {
  loaderUrl: string
  environment: string
  release: string
}

const validated = (raw: string | undefined, pattern: RegExp): string => {
  const value = raw?.trim() ?? ''
  return value && pattern.test(value) ? value : ''
}

/** Loader Script URL. Empty string when unset or malformed. */
export const sentryLoaderUrl = (): string =>
  validated(process.env.NEXT_PUBLIC_SENTRY_LOADER_URL, SENTRY_LOADER_URL_PATTERN)

/** Environment name reported with every event; falls back to `NODE_ENV`. */
export const sentryEnvironment = (): string =>
  validated(process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT, SENTRY_ENVIRONMENT_PATTERN) ||
  process.env.NODE_ENV ||
  'development'

/**
 * Whether this build reports errors at all. A valid loader URL is necessary but
 * not sufficient: outside production the operator must also set
 * `NEXT_PUBLIC_SENTRY_ENABLED=true`, which is what lets a LOCAL dev server (or
 * a one-off build) opt in without editing code.
 *
 * That flag is NOT what keeps Vercel Preview quiet. A Preview build also runs
 * with `NODE_ENV=production`, so the production branch here is taken there too.
 * What keeps Preview silent is that Vercel scopes environment variables per
 * environment and `NEXT_PUBLIC_SENTRY_LOADER_URL` is set only in the Production
 * scope — a Preview build has no URL, so {@link sentryLoaderUrl} is empty and
 * nothing is injected. Setting that variable at "All Environments" would send
 * every preview's errors to the same issue stream as production.
 */
export const isErrorReportingEnabled = (): boolean =>
  sentryLoaderUrl().length > 0 &&
  (process.env.NODE_ENV === 'production' || process.env.NEXT_PUBLIC_SENTRY_ENABLED === 'true')

/** The full config, or `null` when error reporting is off for this build. */
export function resolveErrorReportingConfig(): ErrorReportingConfig | null {
  if (!isErrorReportingEnabled()) return null

  return {
    loaderUrl: sentryLoaderUrl(),
    environment: sentryEnvironment(),
    release: WEB_SENTRY_RELEASE,
  }
}
